/**
 * UserHeader Component (Refactored)
 *
 * Uses the centralised AuthContext and API client for authentication.
 * This is the production-ready version with improved security practices.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { apiClient, ApiRequestError } from '../services/api';
import { CountriesResponseSchema } from '../schemas';
import type { Country } from '../schemas';
import { OrganisationContextIndicator } from './DataSharingIndicator';
import PasswordInput from './PasswordInput';
import { useTopBarSlotContent } from '../contexts/TopBarSlotContext';
import { useUpgradeModal } from './UpgradeModal';
import Button from './ui/Button';
import Field from './ui/Field';
import Input from './ui/Input';
import Modal from './ui/Modal';
import './UserHeader.css';
import Badge from './ui/Badge';
import Tabs from './ui/Tabs';
import { humanizeLabel } from '../services/formatting';

interface UserHeaderProps {
  onUserChange?: (user: unknown) => void;
  onMenuToggle?: () => void;
}

const UserHeaderNew: React.FC<UserHeaderProps> = ({ onUserChange, onMenuToggle }) => {
  const {
    user,
    isAuthenticated,
    isLoading: authLoading,
    selectedOrganisationId,
    login,
    register,
    logout,
    selectOrganisation,
    getSelectedOrganisation,
    hasMultipleOrganisations,
  } = useAuth();

  const { currentRole, userPermissions } = usePermissions();
  const { openCustom } = useUpgradeModal();
  const topBarContent = useTopBarSlotContent();
  const navigate = useNavigate();

  // Account tier, read from the selected org's raw entitlements (mirrors the
  // backend tier_for_entitlements and the Navbar). It re-derives whenever the
  // org changes, since PermissionContext refetches on org switch. The badge
  // lives by the profile icon — not in the org switcher — so it reads as "your
  // account", and clicking it opens the upgrade prompt (later: choose-a-plan).
  const entitled = userPermissions.organisation_entitled_features;
  const tier: 'free' | 'pro' | 'enterprise' = entitled.includes('all_frameworks')
    ? 'enterprise'
    : entitled.includes('reporting')
      ? 'pro'
      : 'free';
  const tierLabel = tier === 'enterprise' ? 'Enterprise' : tier === 'pro' ? 'Pro' : 'Free';
  const openUpgrade = () =>
    openCustom(
      'Upgrade your plan',
      `You're on the ${tierLabel} plan. Upgrading unlocks more of Econori — ` +
        'reporting, frameworks, measurement and more. Choosing and paying for a ' +
        'plan in-app is coming soon; in the meantime, get in touch and we’ll ' +
        'move your account across.',
    );
  const location = useLocation();
  // Hide the chrome Login/Register button when the user is on a supplier
  // engagement form (URL carries ?token=…). The form's in-context register
  // link preserves engagement state so the user is attached to the right org.
  const isEngagementMode = useMemo(
    () => new URLSearchParams(location.search).get('token') !== null,
    [location.search],
  );

  // Modal state
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');

  // Auth form state
  const [modalEmail, setModalEmail] = useState<string>('');
  const [modalPassword, setModalPassword] = useState<string>('');
  const [modalPasswordConfirm, setModalPasswordConfirm] = useState<string>('');
  const [modalFirstName, setModalFirstName] = useState<string>('');
  const [modalLastName, setModalLastName] = useState<string>('');
  const [modalJobTitle, setModalJobTitle] = useState<string>('');
  const [isModalAuthLoading, setIsModalAuthLoading] = useState<boolean>(false);
  const [modalAuthError, setModalAuthError] = useState<string | null>(null);

  // Organisation creation state (for registration)
  const [newOrganisationName, setNewOrganisationName] = useState<string>('');
  const [newOrganisationCountryId, setNewOrganisationCountryId] = useState<string>('');
  // #5: free-text the country name to type-and-filter; the id (above) is
  // resolved from an exact name match and stays the source of truth.
  const [countryQuery, setCountryQuery] = useState<string>('');
  const [countries, setCountries] = useState<Country[]>([]);

  // Similar-org suggestions for the register tab. Fetched on blur of the org
  // name input so we don't query on every keystroke. Clicking "Claim this
  // org" hands off to LoginPage with the chosen org pre-filled, which has the
  // full claim-flow UI (claim reason textarea, register-for-org call).
  type SimilarOrgCandidate = {
    id: number;
    name: string;
    website: string;
    registered_address: string;
    registered_country: string;
    registered_country_id: number | null;
    registered_company_number: string;
    status: string;
  };
  const [similarOrgs, setSimilarOrgs] = useState<SimilarOrgCandidate[]>([]);

  const fetchSimilarOrgs = async (rawName: string) => {
    const query = (rawName || '').trim();
    if (query.length < 3) {
      setSimilarOrgs([]);
      return;
    }
    try {
      const data = await apiClient.get<{ organisations: SimilarOrgCandidate[] }>(
        `/api/organisations/suggest-similar/?q=${encodeURIComponent(query)}`,
      );
      setSimilarOrgs(data.organisations || []);
    } catch {
      setSimilarOrgs([]);
    }
  };

  const handleClaimSuggestion = (candidate: SimilarOrgCandidate): void => {
    setShowAuthModal(false);
    setSimilarOrgs([]);
    navigate('/login', {
      state: {
        initialMode: 'register',
        prefillOrg: {
          id: candidate.id,
          name: candidate.name,
          countryId: candidate.registered_country_id,
        },
      },
    });
  };

  // Organisation selector state
  const [organisationSearchQuery, setOrganisationSearchQuery] = useState<string>('');
  const [isOrgMenuOpen, setIsOrgMenuOpen] = useState(false);
  const orgComboRef = useRef<HTMLDivElement | null>(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await apiClient.get<{ countries: Country[] }>(
          '/api/organisations/countries/'
        );
        const validated = CountriesResponseSchema.safeParse(response);
        if (validated.success) {
          setCountries(validated.data.countries);
        }
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    };
    fetchCountries();
  }, []);

  // Reset organisation fields when closing modal
  useEffect(() => {
    if (!showAuthModal) {
      setNewOrganisationName('');
      setNewOrganisationCountryId('');
      setCountryQuery('');
    }
  }, [showAuthModal]);

  // Handle click outside profile menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
      if (orgComboRef.current && !orgComboRef.current.contains(event.target as Node)) {
        setIsOrgMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Notify parent of user changes
  useEffect(() => {
    if (onUserChange) {
      onUserChange(user);
    }
  }, [user, onUserChange]);

  const handleLogin = async (): Promise<void> => {
    const email = modalEmail.trim().toLowerCase();
    const password = modalPassword;

    setModalAuthError(null);

    if (!email || !email.includes('@')) {
      setModalAuthError('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setModalAuthError('Password is required.');
      return;
    }

    setIsModalAuthLoading(true);
    try {
      await login(email, password);
      closeModal();
    } catch (err) {
      console.error('Error logging in:', err);
      if (err instanceof ApiRequestError) {
        setModalAuthError(err.message);
      } else {
        setModalAuthError('Login failed. Please try again.');
      }
    } finally {
      setIsModalAuthLoading(false);
    }
  };

  const handleRegister = async (): Promise<void> => {
    const email = modalEmail.trim().toLowerCase();
    const password = modalPassword;
    const passwordConfirm = modalPasswordConfirm;
    const firstName = modalFirstName.trim();
    const lastName = modalLastName.trim();

    setModalAuthError(null);

    // Validation
    if (!email || !email.includes('@')) {
      setModalAuthError('Please enter a valid email address.');
      return;
    }
    if (!password || password.length < 8) {
      setModalAuthError('Password must be at least 8 characters.');
      return;
    }
    if (password !== passwordConfirm) {
      setModalAuthError('Passwords do not match.');
      return;
    }
    if (!firstName) {
      setModalAuthError('First name is required.');
      return;
    }
    if (!lastName) {
      setModalAuthError('Last name is required.');
      return;
    }
    if (!modalJobTitle.trim()) {
      setModalAuthError('Job title is required.');
      return;
    }
    if (!newOrganisationName.trim()) {
      setModalAuthError('Organisation name is required.');
      return;
    }
    if (!newOrganisationCountryId) {
      setModalAuthError('Registered country is required.');
      return;
    }

    setIsModalAuthLoading(true);
    try {
      await register({
        email,
        password,
        passwordConfirm,
        firstName,
        lastName,
        jobTitle: modalJobTitle.trim(),
        organisationName: newOrganisationName.trim(),
        organisationCountryId: parseInt(newOrganisationCountryId, 10),
      });
      closeModal();
    } catch (err) {
      console.error('Error registering:', err);
      if (err instanceof ApiRequestError) {
        setModalAuthError(err.message);
      } else {
        setModalAuthError('Registration failed. Please try again.');
      }
    } finally {
      setIsModalAuthLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleOrganisationChange = (orgId: number) => {
    selectOrganisation(orgId);
    setOrganisationSearchQuery('');
  };

  const closeModal = () => {
    setShowAuthModal(false);
    setModalEmail('');
    setModalPassword('');
    setModalPasswordConfirm('');
    setModalFirstName('');
    setModalLastName('');
    setModalJobTitle('');
    setModalAuthError(null);
  };

  const selectedOrganisation = getSelectedOrganisation();

  const getFilteredOrganisations = () => {
    if (!user?.organisations) return [];

    if (!organisationSearchQuery.trim()) {
      return user.organisations;
    }

    const query = organisationSearchQuery.toLowerCase();
    const filtered = user.organisations.filter((org) =>
      org.name.toLowerCase().includes(query)
    );

    if (selectedOrganisationId && !filtered.some((org) => org.id === selectedOrganisationId)) {
      const selected = user.organisations.find((org) => org.id === selectedOrganisationId);
      if (selected) {
        return [selected, ...filtered];
      }
    }
    return filtered;
  };

  if (authLoading) {
    return (
      <div className="user-header">
        <div className="user-header-content">
          <span style={{ color: 'var(--text-secondary)' }}>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="user-header">
        <div className="user-header-content">
          <button
            type="button"
            className="hamburger-btn"
            onClick={onMenuToggle}
            aria-label="Toggle menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          {/* Context slot: routed pages (e.g. a reporting dashboard) project
              their title + filters into the top bar's dead space here. Empty
              on pages that don't opt in. */}
          <div className="topbar-context-slot">{topBarContent}</div>
          {isAuthenticated && user ? (
            <>
              {hasMultipleOrganisations() ? (
                <div className="org-combobox" ref={orgComboRef}>
                  <button
                    type="button"
                    className="org-combobox-trigger"
                    onClick={() => setIsOrgMenuOpen((o) => !o)}
                    aria-haspopup="listbox"
                    aria-expanded={isOrgMenuOpen}
                    title={selectedOrganisation?.name}
                  >
                    <span className="org-combobox-label">
                      {selectedOrganisation?.name || 'Select organisation'}
                    </span>
                    <span className="org-combobox-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </button>
                  {isOrgMenuOpen && (
                    <div className="org-combobox-menu" role="listbox">
                      <input
                        type="text"
                        className="org-combobox-search"
                        placeholder="Search organisations…"
                        value={organisationSearchQuery}
                        autoFocus
                        onChange={(e) =>
                          setOrganisationSearchQuery(e.target.value)
                        }
                      />
                      <ul className="org-combobox-list">
                        {getFilteredOrganisations().length > 0 ? (
                          getFilteredOrganisations().map((org) => (
                            <li key={org.id}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={
                                  org.id === selectedOrganisationId
                                }
                                className={
                                  'org-combobox-option' +
                                  (org.id === selectedOrganisationId
                                    ? ' is-selected'
                                    : '')
                                }
                                onClick={() => {
                                  handleOrganisationChange(org.id);
                                  setIsOrgMenuOpen(false);
                                }}
                              >
                                {org.name}
                              </button>
                            </li>
                          ))
                        ) : (
                          <li className="org-combobox-empty">
                            No organisations found
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                selectedOrganisation && (
                  <OrganisationContextIndicator />
                )
              )}

              {/* Role badge */}
              {currentRole && (
                <Badge tone="outline" title={`Your role: ${humanizeLabel(currentRole)}`}>
                  {currentRole}
                </Badge>
              )}

              {/* Account-tier badge, by the profile icon. Enterprise is the top
                  tier, so it's a static badge; Free/Pro are a button that opens
                  the upgrade prompt. */}
              {tier === 'enterprise' ? (
                <span
                  className="tier-badge tier-badge-enterprise"
                  title="Your account plan: Enterprise"
                >
                  {tierLabel}
                </span>
              ) : (
                <button
                  type="button"
                  className={`tier-badge tier-badge-${tier} tier-badge-button`}
                  title={`Your account plan: ${tierLabel} — upgrade for more`}
                  onClick={openUpgrade}
                >
                  {tierLabel}
                </button>
              )}

              <div className="profile-menu" ref={profileMenuRef}>
                <button
                  type="button"
                  className="profile-button"
                  onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                  title={user.email}
                >
                  <div className="profile-icon-container">
                    <span className="profile-initials">
                      {(
                        (user.first_name?.[0] || '') +
                        (user.last_name?.[0] || '')
                      ).toUpperCase() ||
                        (user.email?.[0] || '?').toUpperCase()}
                    </span>
                  </div>
                </button>
                {isProfileMenuOpen && (
                  <div className="profile-dropdown">
                    <div className="profile-dropdown-user">
                      <div className="profile-dropdown-name">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="profile-dropdown-email">{user.email}</div>
                      {user.is_admin && (
                        <Badge tone="outline" className="profile-dropdown-badge">System Admin</Badge>
                      )}
                    </div>
                    <button
                      type="button"
                      className="profile-dropdown-action"
                      onClick={() => {
                        navigate('/account');
                        setIsProfileMenuOpen(false);
                      }}
                    >
                      Account Settings
                    </button>
                    <button
                      type="button"
                      className="profile-dropdown-action"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Button
              variant="primary"
              onClick={() => {
                setAuthMode('login');
                setShowAuthModal(true);
              }}
            >
              {isEngagementMode ? 'Login' : 'Login / Register'}
            </Button>
          )}
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <Modal
          open
          onClose={closeModal}
          title={authMode === 'login' ? 'Login' : 'Register'}
          closeOnBackdrop={false}
          footer={
            <Button
              variant="primary"
              block
              onClick={authMode === 'login' ? handleLogin : handleRegister}
              loading={isModalAuthLoading}
            >
              {isModalAuthLoading
                ? 'Processing...'
                : authMode === 'login'
                ? 'Login'
                : 'Register'}
            </Button>
          }
        >

          <Tabs
            variant="segmented"
            fill
            ariaLabel="Login or register"
            items={[
              { id: 'login', label: 'Login' },
              { id: 'register', label: 'Register' },
            ]}
            value={authMode}
            onChange={setAuthMode}
          />

          {modalAuthError && (
            <div style={{ padding: '0.75rem', background: 'var(--feedback-error-bg)', color: 'var(--feedback-error)', borderRadius: '4px' }}>
              {modalAuthError}
            </div>
          )}

            {authMode === 'register' && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '1rem' }}>
                    Organisation <span style={{ color: 'var(--feedback-error)' }}>*</span>
                  </label>

                  <Field label="Organisation Name" required>
                    <Input
                      type="text"
                      value={newOrganisationName}
                      onChange={(e) => {
                        setNewOrganisationName(e.target.value);
                        if (similarOrgs.length > 0) setSimilarOrgs([]);
                      }}
                      onBlur={(e) => { void fetchSimilarOrgs(e.target.value); }}
                      placeholder="Enter organisation name"
                    />
                  </Field>

                  {/* Sits between Org Name and Country so the suggestion
                      list isn't visually buried under the country dropdown
                      when it expands. */}
                  {similarOrgs.length > 0 && (
                    <div style={{
                      padding: '12px 16px',
                      background: 'var(--brand-accent-light, #ffe7ef)',
                      border: '1px solid var(--brand-accent, #ff1261)',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      color: 'var(--brand-accent, #ff1261)',
                      lineHeight: 1.5,
                    }}>
                      <p style={{ margin: '0 0 0.75rem 0' }}>
                        If you see an organisation that looks like yours,
                        please either email your internal admin to be added
                        as a user, or register for a free account to claim
                        ownership of the selected organisation. Once your
                        claim has been verified, you will be able to manage
                        your account and edit your official org name via
                        the settings.
                      </p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {similarOrgs.map((org) => (
                          <li
                            key={org.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '0.75rem',
                              padding: '0.5rem 0.75rem',
                              marginBottom: '0.4rem',
                              background: 'white',
                              border: '1px solid var(--brand-accent-mid, #ffa0c0)',
                              borderRadius: '4px',
                              color: 'var(--text-primary)',
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                              <span style={{ fontWeight: 600 }}>{org.name}</span>
                              {(org.registered_address || org.registered_country) && (
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                  {[org.registered_address, org.registered_country].filter(Boolean).join(' · ')}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleClaimSuggestion(org)}
                              style={{
                                padding: '0.4rem 0.75rem',
                                background: 'var(--brand-accent, #ff1261)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Claim this org
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div>
                    <Field label="Registered Country" required>
                      <Input
                        type="text"
                        list="registered-country-options"
                        value={countryQuery}
                        onChange={(e) => {
                          const q = e.target.value;
                          setCountryQuery(q);
                          // Resolve the id from an exact (case-insensitive) name
                          // match; no match → empty, so validation still catches it.
                          const match = countries.find(
                            (c) => c.name.toLowerCase() === q.trim().toLowerCase(),
                          );
                          setNewOrganisationCountryId(match ? String(match.id) : '');
                        }}
                        placeholder="Type to search countries…"
                        autoComplete="off"
                      />
                    </Field>
                    <datalist id="registered-country-options">
                      {countries.map((country) => (
                        <option key={country.id} value={country.name} />
                      ))}
                    </datalist>
                  </div>
                </div>

                <div style={{ paddingTop: '1rem', borderTop: '1px solid var(--border-subtle)' }}>
                  <Field label="First Name" required>
                    <Input
                      type="text"
                      value={modalFirstName}
                      onChange={(e) => setModalFirstName(e.target.value)}
                    />
                  </Field>
                </div>
                <Field label="Last Name" required>
                  <Input
                    type="text"
                    value={modalLastName}
                    onChange={(e) => setModalLastName(e.target.value)}
                  />
                </Field>

                <Field label="Job Title" required>
                  <Input
                    type="text"
                    value={modalJobTitle}
                    onChange={(e) => setModalJobTitle(e.target.value)}
                    placeholder="e.g. Sustainability Manager"
                  />
                </Field>
              </>
            )}
            <Field label="Email" required>
              <Input
                type="email"
                value={modalEmail}
                onChange={(e) => setModalEmail(e.target.value)}
              />
            </Field>
            <Field label="Password" required>
              <PasswordInput
                className="ui-input"
                value={modalPassword}
                onChange={(e) => setModalPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isModalAuthLoading) {
                    if (authMode === 'login') {
                      handleLogin();
                    }
                  }
                }}
              />
            </Field>
            {authMode === 'register' && (
              <Field label="Confirm Password" required>
                <PasswordInput
                  className="ui-input"
                  value={modalPasswordConfirm}
                  onChange={(e) => setModalPasswordConfirm(e.target.value)}
                />
              </Field>
            )}

            {authMode === 'register' && (
              <div style={{ padding: '1rem', background: 'var(--surface-sunken)', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  <strong style={{ color: 'var(--text-primary)', display: 'block', marginBottom: '0.5rem' }}>We'll use your details to:</strong>
                  <ul style={{ margin: 0, paddingLeft: '1.5rem', listStyle: 'disc' }}>
                    <li>Save your ESG assessment</li>
                    <li>Let you resume later</li>
                    <li>Provide your results</li>
                  </ul>
                </div>
              </div>
            )}

        </Modal>
      )}
    </>
  );
};

export default UserHeaderNew;
